import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded'
import VisibilityIcon from '@mui/icons-material/Visibility'
import {
  Box,
  Button,
  ButtonBase,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Typography,
} from '@mui/material'
import React, { useRef, useState, useEffect, useMemo } from 'react'
import {
  ArrayField,
  Edit,
  useNotify,
  useRecordContext,
  useRefresh,
  WithRecord,
} from 'react-admin'
import { useLocation } from 'react-router-dom'
import { sendLog } from '../../Class/AuditLog'
import AdminNotesPanel from '../../Components/QuoteRequest/Edit/AdminNotesPanel'
import CapPanel from '../../Components/QuoteRequest/Edit/CapPanel'
import CapPanelML from '../../Components/QuoteRequest/Edit/CapPanel/mlpricing'
import CustomerNotesPanel from '../../Components/QuoteRequest/Edit/CustomerNotesPanel'
import VehicleNotesPanel from '../../Components/QuoteRequest/Edit/VehicleNotesPanel'
import BookInPanel from '../../Components/QuoteRequest/Edit/BookInPanel'
import MitelDataPanel from '../../Components/QuoteRequest/Edit/MitelDataPanel'
import {
  CollectionPanel,
  CustomerInfoPanel,
  HistoryTabbedPanel,
  LeadIDPanel,
  VehicleDetailsPanel,
  ViewAgentsPanel,
} from '../../Components/QuoteRequest/Edit/Panels'
import PricingNotesPanel from '../../Components/QuoteRequest/Edit/PricingNotesPanel'
import VehicleAdjustments from '../../Components/VehicleAdjustments'
import { getNextLead } from '../../Utils/QuoteRequestUtils'
import {
  getAdjustmentsSettings,
  handleLeadLocking,
  saveVehicleNote,
} from '../../Utils/RestUtils'
import { SalvageBooking } from '../../Components/QuoteRequest'
import { ArrowBackIosRounded } from '@mui/icons-material'
import { useGetIdentity } from 'ra-core'
import CollectorLogicPanel from '../../Components/QuoteRequest/Edit/CollectorLogicPanel'
import MLPricingPanel from '../../Components/QuoteRequest/Edit/MLPricingPanel'
import AdjustmentStatusPanel from '../../Components/QuoteRequest/Edit/AdjustmentStatusPanel'
import { PriceModalProvider } from '../../Utils/providers/PriceModalProvider'
import { AdjustmentStatusProvider } from '../../Utils/providers/AdjustmentStatusProvider'
import PriceModals from '../../Components/QuoteRequest/Edit/PriceModals'

// NOTE - causes rendering issues so moved outside of `EditQuoteRequest`
// move to a separate file eventually
const Adjustments = () => {
  const record = useRecordContext()
  const refresh = useRefresh()

  const [adjustmentsSettings, setAdjustmentsSettings] = useState([])

  useEffect(() => {
    const fetchSettings = async () => {
      const adjSettings = await getAdjustmentsSettings()
      setAdjustmentsSettings(adjSettings)
    }
    fetchSettings()
  }, [])

  useEffect(() => {
    const handler = () => refresh()
    window.addEventListener('refreshRecord', handler)
    return () => window.removeEventListener('refreshRecord', handler)
  }, [refresh])

  const sortedAdjustments = useMemo(() => {
    const vehicleAdjustments =
      record?.leadContact?.vehicle?.vehicleAdjustments || []
    if (!vehicleAdjustments.length || !adjustmentsSettings.length) return []

    const sorted = []

    vehicleAdjustments.forEach((vehicleAdjustment) => {
      const matchedAdjustmentSettings = adjustmentsSettings.find(
        (setting) => setting['@id'] === vehicleAdjustment.adjustment
      )
      if (!matchedAdjustmentSettings) return

      const adjustmentIdentifier =
        matchedAdjustmentSettings.adjustmentIdentifier === 'damage'
          ? 'damage'
          : 'general'

      const index = sorted.findIndex((el) => el.type === adjustmentIdentifier)
      const adjName = matchedAdjustmentSettings.adjustmentName

      if (index === -1) {
        sorted.push({
          type: adjustmentIdentifier,
          adjustments: {
            [adjName]: {
              adjustment: vehicleAdjustment,
              adjustmentSettings: matchedAdjustmentSettings,
            },
          },
        })
      } else {
        sorted[index].adjustments[adjName] = {
          adjustment: vehicleAdjustment,
          adjustmentSettings: matchedAdjustmentSettings,
        }
      }
    })

    return sorted
  }, [record?.leadContact?.vehicle?.vehicleAdjustments, adjustmentsSettings])

  if (!sortedAdjustments.length) return null

  return (
    <>
      <ArrayField
        source="vehicleAdjustments"
        label={false}
        className="vehicle-adjustments"
      >
        <VehicleAdjustments sortedAdjustments={sortedAdjustments} />
      </ArrayField>
      <PriceModals sortedAdjustments={sortedAdjustments} />
    </>
  )
}

// Show old cap panel by user/role
// Kirk Hemmings
const CAP_PANEL_ALLOWED_USER_IDS = new Set(['1106'])
const CAP_PANEL_ALLOWED_ROLES = ['ROLE_SALVAGE']
// Show Collector Logic panel by user
// Hina Shaikh
const CL_PANEL_ALLOWED_USER_IDS = new Set(['196'])

const EditQuoteRequest = (props) => {
  const refresh = useRefresh()
  const [historyPanelSelectedTab, setHistoryPanelSelectedTab] = useState(0)
  const [notes, setNotes] = useState({
    salvageNotes: null,
    quoteNotes: null,
  })
  const [postcodeChanged, setPostcodeChanged] = useState(false)
  const notify = useNotify()
  const notesRef = useRef()
  const overlayRef = useRef(null)
  const { identity } = useGetIdentity()

  const roles = localStorage.getItem('permissions').split(',')
  const user_id = localStorage.getItem('user_id')
  const showCLPanel = CL_PANEL_ALLOWED_USER_IDS.has(user_id)
  const showCapPanel =
    CAP_PANEL_ALLOWED_ROLES.some((role) => roles.includes(role)) ||
    CAP_PANEL_ALLOWED_USER_IDS.has(user_id)

  const { state } = useLocation()
  let defaultView = 'list'

  if (state != null && state.contactLogScreen != null) {
    defaultView = state.contactLogScreen
  }

  const [contactLogScreen, setContactLogScreen] = useState(defaultView)

  const saveNewVehicleNote = async (newVehicleNote, originId) => {
    notesRef.current = notes
    const response = await saveVehicleNote({
      leadId: originId,
      payload: newVehicleNote,
    })

    if (response.result === 'success') {
      sendLog(
        originId,
        identity.fullName + ' added vehicle note: ' + newVehicleNote
      )
      setNotes({
        salvageNotes: notes.salvageNotes,
        quoteNotes: newVehicleNote,
      })
    } else {
      notify(response.error_msg, { type: 'error' })
    }
  }

  const transformData = (data) => {
    data.leadContact.vehicle.vehicleInfo.alloyWheels = parseInt(
      data.leadContact.vehicle.vehicleInfo.alloyWheels
    )

    data.leadContact.vehicle.vehicleInfo.previousKeepers = parseInt(
      data.leadContact.vehicle.vehicleInfo.previousKeepers
    )

    data.quotePaymentDetails.acc = parseInt(data.quotePaymentDetails.acc)

    data.quotePaymentDetails.sort = parseInt(data.quotePaymentDetails.sort)
    return data
  }

  const GetNextButton = (props) => {
    const [disableButtons, setDisableButtons] = useState(false)
    const record = useRecordContext(props)

    const permissions = (localStorage.getItem('permissions') || '').split(',')
    const isSalesRole = ['ROLE_SALES_EXEC', 'ROLE_TEAM_LEAD'].some((role) =>
      permissions.includes(role)
    )

    const handleCloseLeadClick = async (record) => {
      setDisableButtons(true)
      sendLog(record.originId, 'Close lead button clicked', 'LOG_GET_NEXT')
      if (
        confirm(
          'This will log you out of this lead, are you finished working on this lead?'
        )
      ) {
        sendLog(record.originId, 'Close lead confirmed', 'LOG_GET_NEXT')

        handleLeadLocking('unlock', record.originId)

        location.href = '/'
      }
      setDisableButtons(false)
    }

    const handleButtonClick = async (record) => {
      setDisableButtons(true)
      sendLog(record.originId, 'Get next button clicked', 'LOG_GET_NEXT')
      if (
        confirm(
          'This will log you out of this lead, are you finished working on this lead?'
        )
      ) {
        sendLog(record.originId, 'Get next confirmed', 'LOG_GET_NEXT')
        const result = await getNextLead(record.originId)
        if (result.status === 'success') {
          handleLeadLocking('lock', result.id)
          location.href =
            '/#/quote-requests/%2Fapi%2Fquote-requests%2F' + result.id
          refresh()
        } else {
          notify(result.message, { type: 'error' })
        }
      }
      setDisableButtons(false)
    }

    return (
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          gridColumn: '1 / span 6',
          gridRowEnd: 1,
          gridRowStart: 1,
          justifyContent: 'space-between',
          mt: 2,
        }}
      >
        <Button
          sx={{
            borderWidth: '0px !important',
          }}
          disabled={disableButtons}
          variant="contained"
          startIcon={<ArrowBackIosRounded />}
          onClick={() => handleCloseLeadClick(record)}
        >
          Back to dashboard
        </Button>
        <Button
          sx={{
            borderWidth: '0px !important',
            display: isSalesRole ? 'none' : 'inherit',
          }}
          disabled={disableButtons}
          variant="contained"
          endIcon={<ArrowForwardIosRoundedIcon />}
          onClick={() => handleButtonClick(record)}
        >
          Get next lead
        </Button>
      </Box>
    )
  }

  const DisableLead = (props) => {
    const record = useRecordContext(props)
    if (
      record.leadContact.vehicle.vehicleCollection &&
      record.leadContact.vehicle.vehicleCollection.offerSelected.collector
    ) {
      return (
        <Box
          sx={{
            background: 'black',
            height: 'calc(100vh + 50px)',
            opacity: '0.5',
            display: 'block',
            position: 'fixed',
            top: '-50px',
            width: '100vw',
            zIndex: '99999',
          }}
        ></Box>
      )
    }
  }

  return (
    <PriceModalProvider>
      <Edit
        {...props}
        title="Edit Lead"
        actions={false}
        redirect={false}
        transform={transformData}
        component="div"
        sx={{ '& .RaEdit-noActions': { marginTop: '0 !important' } }}
      >
        <AdjustmentStatusProvider>
          <Grid
            sx={{
              display: 'grid',
              gap: '0.5rem',
              gridAutoFlow: 'row dense',
              gridTemplateColumns: 'repeat(6, 1fr)',
            }}
          >
            <GetNextButton {...props} />
            <Grid
              sx={{ gridColumn: '1 / span 2', gridRowStart: 2, gridRowEnd: 2 }}
            >
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ paddingBottom: '0' }}>
                  <LeadIDPanel {...props} />
                </CardContent>
              </Card>
            </Grid>
            <Grid
              sx={{ gridColumn: '3 / span 2', gridRowStart: 2, gridRowEnd: 2 }}
            >
              <Card sx={{ height: '100%', position: 'relative' }}>
                <WithRecord
                  render={(record) => {
                    // If the lead is SC and the user is salvage then the customer details must be obscured
                    if (
                      roles.includes('ROLE_SALVAGE') &&
                      record.leadContact.vehicle.vehicleSalvage.assignedTo ==
                        'cars'
                    ) {
                      return (
                        <>
                          <Box
                            ref={overlayRef}
                            sx={{
                              backgroundColor: 'rgba(#000, 0.6)',
                              backdropFilter: 'blur(4px)',
                              height: '100%',
                              position: 'absolute',
                              width: '100%',
                              zIndex: 1000,
                            }}
                          >
                            <Box
                              sx={{
                                alignItems: 'center',
                                display: 'flex',
                                justifyContent: 'center',
                                height: '100%',
                              }}
                            >
                              <ButtonBase
                                onClick={() => {
                                  sendLog(
                                    record.originId,
                                    `User ${identity.fullName} viewed customer details panel`
                                  )
                                  refresh()
                                  overlayRef.current.remove()
                                }}
                                sx={{ display: 'flex', flexWrap: 'wrap' }}
                              >
                                <VisibilityIcon />
                                <Typography sx={{ ml: 1 }}>
                                  Click to reveal
                                </Typography>
                                <Typography width={'100%'} fontSize="10px">
                                  This action will be logged
                                </Typography>
                              </ButtonBase>
                            </Box>
                          </Box>
                        </>
                      )
                    }
                  }}
                />
                <CardContent>
                  <CustomerInfoPanel
                    {...props}
                    setContactLogScreen={setContactLogScreen}
                    setHistoryPanelSelectedTab={setHistoryPanelSelectedTab}
                    setPostCodeChanged={setPostcodeChanged}
                  />
                </CardContent>
              </Card>
            </Grid>
            <Grid
              sx={{ gridColumn: '5 / span 2', gridRowStart: 2, gridRowEnd: 4 }}
            >
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <VehicleDetailsPanel />
                </CardContent>
              </Card>
            </Grid>
            <Grid
              sx={{ gridColumn: '1 / span 4', gridRowStart: 3, gridRowEnd: 4 }}
            >
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                }}
              >
                <CardContent sx={{ width: '100%' }}>
                  <CollectionPanel />
                </CardContent>
              </Card>
            </Grid>

            <WithRecord
              render={(record) => {
                const isSalvage = record.leadContact.vehicle.tooGoodToScrap
                const roles = localStorage.getItem('permissions').split(',')

                if (
                  roles.includes('ROLE_ADMIN') ||
                  roles.includes('ROLE_SALVAGE') ||
                  roles.includes('ROLE_SHORE')
                ) {
                  return (
                    <Grid
                      sx={{
                        gridColumn: '1 / span 6',
                        gridRow: 4,
                      }}
                    >
                      <Card>
                        <CardContent>
                          <SalvageBooking userRoles={roles} />
                        </CardContent>
                      </Card>
                    </Grid>
                  )
                }
              }}
            />

            <WithRecord
              render={(record) => {
                if (
                  showCLPanel &&
                  record.leadContact.vehicle.vehicleCollection &&
                  record.leadContact.vehicle.vehicleCollection.offerSelected &&
                  record.leadContact.vehicle.vehicleCollection.offerSelected
                    .collector.collectorLogicEnabled === 1 &&
                  (record.status === 'Collected' ||
                    record.status === 'Lead declined')
                )
                  return (
                    <Grid
                      sx={{
                        gridColumn: '1 / span 6',
                      }}
                    >
                      <Card>
                        <CardContent>
                          <CollectorLogicPanel />
                        </CardContent>
                      </Card>
                    </Grid>
                  )
              }}
            />

            <Grid
              sx={{
                gridColumn: '1 / span 3',
                gridRow: 'auto / span 2',
              }}
            >
              <Card sx={{ height: '100%', position: 'relative' }}>
                <CardContent sx={{ padding: '0 !important' }}>
                  <div id="start-call-container"></div>
                  <HistoryTabbedPanel
                    {...props}
                    setContactLogScreen={setContactLogScreen}
                    contactLogScreen={contactLogScreen}
                    setHistoryPanelSelectedTab={setHistoryPanelSelectedTab}
                    historyPanelSelectedTab={historyPanelSelectedTab}
                  />
                </CardContent>
              </Card>
            </Grid>
            <Grid
              sx={{
                gridColumn: '4 / span 3',
              }}
            >
              <Card sx={{ height: '100%' }}>
                <CardHeader
                  title="Admin Notes"
                  sx={{
                    padding: '10px',
                    marginBottom: '1rem',
                  }}
                  titleTypographyProps={{
                    sx: {
                      marginBottom: '-15px',
                      textTransform: 'uppercase',
                      fontSize: '14px',
                      fontWeight: 'bold',
                    },
                  }}
                />
                <CardContent
                  sx={{
                    paddingTop: '0',
                    '&:last-child': {
                      paddingBottom: '18px',
                    },
                  }}
                >
                  <AdminNotesPanel />
                </CardContent>
              </Card>
            </Grid>
            <Grid sx={{ gridColumn: '4 / span 3' }}>
              <Card sx={{ height: '100%' }}>
                <CardHeader
                  title="Pricing Notes"
                  sx={{
                    padding: '10px',
                  }}
                  titleTypographyProps={{
                    sx: {
                      marginBottom: '-15px',
                      textTransform: 'uppercase',
                      fontSize: '14px',
                      fontWeight: 'bold',
                    },
                  }}
                />
                <CardContent>
                  <PricingNotesPanel />
                </CardContent>
              </Card>
            </Grid>
            <WithRecord
              render={(record) => {
                const isSalvage = record.leadContact.vehicle.tooGoodToScrap
                if (isSalvage) {
                  return (
                    <React.Fragment>
                      <Grid
                        id="capPanel"
                        sx={{
                          gridColumn: '1 / span 6',
                        }}
                      >
                        <Card>
                          <CardContent>
                            {showCapPanel ? <CapPanel /> : <CapPanelML />}
                          </CardContent>
                        </Card>
                      </Grid>

                      <Grid
                        id="adjustmentStatusPanel"
                        sx={{
                          gridColumn: '1 / span 6',
                        }}
                      >
                        <Card>
                          <CardContent>
                            <AdjustmentStatusPanel />
                          </CardContent>
                        </Card>
                      </Grid>

                      <Grid
                        sx={{
                          gridColumn: '1 / span 6',
                        }}
                      >
                        <Card className="vehicle-adjustments">
                          <CardContent>
                            <Adjustments />
                          </CardContent>
                        </Card>
                      </Grid>

                      <MLPricingPanel />

                      <Grid
                        id="customerNotesPanel"
                        sx={{
                          gridColumn: '1 / span 3',
                        }}
                      >
                        <Card sx={{ height: '100%' }}>
                          <CardContent>
                            <CardHeader
                              title="Customer submitted notes & photos"
                              sx={{
                                padding: '10px',
                                marginBottom: '1rem',
                              }}
                              titleTypographyProps={{
                                sx: {
                                  marginBottom: '-15px',
                                  textTransform: 'uppercase',
                                  fontSize: '14px',
                                  fontWeight: 'bold',
                                },
                              }}
                            />
                            <CustomerNotesPanel />
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid
                        id="vehicleNotesPanel"
                        sx={{
                          gridColumn: '4 / span 3',
                        }}
                      >
                        <Card>
                          <CardContent>
                            <CardHeader
                              title="Vehicle Notes"
                              sx={{
                                padding: '10px',
                                marginBottom: '1rem',
                              }}
                              titleTypographyProps={{
                                sx: {
                                  marginBottom: '-15px',
                                  textTransform: 'uppercase',
                                  fontSize: '14px',
                                  fontWeight: 'bold',
                                },
                              }}
                            />
                            <VehicleNotesPanel
                              notes={notes}
                              saveNewVehicleNote={saveNewVehicleNote}
                              editable={true}
                              setNotes={setNotes}
                            />
                          </CardContent>
                        </Card>
                      </Grid>
                    </React.Fragment>
                  )
                }
              }}
            />

            <Grid
              id="collectionAgentsPanel"
              sx={{
                gridColumn: '1 / span 6',
              }}
            >
              <Card>
                <CardContent sx={{ padding: '0 !important' }}>
                  <ViewAgentsPanel
                    postcodeChanged={postcodeChanged}
                    setPostCodeChanged={setPostcodeChanged}
                  />
                </CardContent>
              </Card>
            </Grid>
            <Grid
              sx={{
                gridColumn: '1 / span 6',
              }}
            >
              <BookInPanel
                notes={notes}
                saveNewVehicleNote={saveNewVehicleNote}
                setNotes={setNotes}
              />
            </Grid>
          </Grid>
          <Grid
            sx={{
              gridColumn: '1 / span 6',
            }}
          >
            <MitelDataPanel />
          </Grid>
        </AdjustmentStatusProvider>
      </Edit>
    </PriceModalProvider>
  )
}
export default EditQuoteRequest
